import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import {
  ChevronLeft, ChevronRight, Loader2, MapPin, Clock,
  Truck, PackageCheck, CheckCircle2, Download, Eraser, Check,
  AlertCircle, Lock, Circle, Send, Copy, MessageSquare, Phone,
  Link2, X, FileDown, RotateCcw, QrCode,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import WizardStepperHorizontal from './WizardStepperHorizontal';
import WizardSidebar from './WizardSidebar';
import PhotoUploadCard from './PhotoUploadCard';
import MerchandiseChecksList from './MerchandiseChecksList';
import SignatureLinkQRModal from './SignatureLinkQRModal';

const LOADING_STEPS = ['Vérification', 'Détails', 'Photos', 'Signatures', 'Récap'];
const UNLOADING_STEPS = ['Vérification', 'Contrôle marchandise', 'Réserves', 'Photos', 'Signature & récap'];

const extractErr = (e, fb = 'Échec') => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fb;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => (typeof x === 'string' ? x : (x?.msg || JSON.stringify(x)))).join(' · ');
  return JSON.stringify(d);
};

/**
 * Wizard 5 étapes pour finaliser UN bon (chargement OU déchargement).
 * PM Spec UI 05/05/2026 — 2 parcours distincts :
 *   - Chargement : Vérification → Détails → Photos → Signatures → Récap
 *   - Déchargement : Vérification → Contrôle marchandise → Réserves → Photos → Signature & récap
 *
 * Layout 2 colonnes : zone principale + sidebar fixe à droite (≥lg).
 */
const LoadingNoteWizard = ({
  type, operation, operatorName, endpointPrefix, authHeaders, onCompleted,
  partyScope = null, linkId = null, noteId = null,
}) => {
  const isLoading = type === 'loading';
  const sitePartyKey = isLoading ? 'shipper' : 'consignee';
  const STEPS = isLoading ? LOADING_STEPS : UNLOADING_STEPS;
  const titleAction = isLoading ? 'chargement' : 'déchargement';
  const Icon = isLoading ? Truck : PackageCheck;

  const lsKey = `bb_wizard_${operation?.id}_${type}`;

  const [stepIndex, setStepIndex] = useState(() => {
    try { return parseInt(localStorage.getItem(`${lsKey}_step`) || '0', 10); }
    catch { return 0; }
  });
  const [completedSteps, setCompletedSteps] = useState([]);
  const [note, setNote] = useState(null);
  const [arrivalGps, setArrivalGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [merchandiseChecks, setMerchandiseChecks] = useState([]);
  const [reserveDescription, setReserveDescription] = useState('');
  const [form, setForm] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`${lsKey}_form`) || '{}');
      return {
        actualDate: saved.actualDate || new Date().toISOString().slice(0, 10),
        arrivalTime: saved.arrivalTime || '',
        startTime: saved.startTime || '',
        endTime: saved.endTime || '',
        actualWeight: saved.actualWeight ?? '',
        actualState: saved.actualState || '',
        observations: saved.observations || '',
      };
    } catch {
      return {
        actualDate: new Date().toISOString().slice(0, 10),
        arrivalTime: '', startTime: '', endTime: '',
        actualWeight: '', actualState: '', observations: '',
      };
    }
  });
  const [saving, setSaving] = useState(false);

  // Persist form & step
  useEffect(() => {
    try { localStorage.setItem(`${lsKey}_form`, JSON.stringify(form)); } catch { /* noop */ }
  }, [form, lsKey]);
  useEffect(() => {
    try { localStorage.setItem(`${lsKey}_step`, String(stepIndex)); } catch { /* noop */ }
  }, [stepIndex, lsKey]);

  const ax = axios.create({ headers: authHeaders || {} });

  // Charge le bon existant
  const fetchExistingNote = useCallback(async () => {
    try {
      const r = await ax.get(endpointPrefix);
      const found = (r.data || []).find(n => n.type === type);
      if (found) {
        setNote(found);
        setForm(f => ({
          ...f,
          actualDate: (found.actualDate || f.actualDate || '').slice(0, 10),
          arrivalTime: found.arrivalTime || f.arrivalTime,
          startTime: found.startTime || f.startTime,
          endTime: found.endTime || f.endTime,
          actualWeight: found.actualWeight ?? f.actualWeight,
          actualState: found.actualState || f.actualState,
          observations: found.observations || f.observations,
        }));
        if (found.arrivalGpsCoords) setArrivalGps(found.arrivalGpsCoords);
        if (found.merchandiseChecks) setMerchandiseChecks(found.merchandiseChecks);
        if (found.reserveDescription) setReserveDescription(found.reserveDescription);
        const c = [];
        if (found.id) c.push(0);
        if (isLoading) {
          if (found.endTime || found.startTime) c.push(1);
          if ((found.photos || []).length >= 2) c.push(2);
          if ((found.signatures || []).length >= 2) c.push(3, 4);
        } else {
          if ((found.merchandiseChecks || []).length >= 4) c.push(1);
          if ((found.merchandiseChecks || []).length >= 4) c.push(2);
          if ((found.photos || []).length >= 2) c.push(3);
          if ((found.signatures || []).length >= 2) c.push(4);
        }
        setCompletedSteps(c);
      }
    } catch { /* silent */ }
  }, [endpointPrefix, type, isLoading]);

  useEffect(() => { fetchExistingNote(); }, [fetchExistingNote]);

  const captureGps = useCallback(() => {
    if (!navigator.geolocation) {
      // Silencieux : l'UI affiche déjà "GPS non capté (optionnel)" en orange
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setArrivalGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGpsLoading(false); },
      () => { setGpsLoading(false); /* Silencieux : l'UI affiche déjà "GPS non capté (optionnel)" */ },
      { timeout: 8000, enableHighAccuracy: true }
    );
  }, []);

  useEffect(() => {
    if (stepIndex === 0 && !arrivalGps && !gpsLoading) captureGps();
  }, [stepIndex, arrivalGps, gpsLoading, captureGps]);

  const ensureNote = async () => {
    if (note) return note;
    // PM Bug fix 17/02/2026 — Si noteId scopé (JWT), réutiliser la note existante
    // au lieu d'en créer une nouvelle (évite duplications en multi-rotation).
    if (noteId) {
      try {
        const list = await ax.get(endpointPrefix);
        const found = (list.data || []).find(n => n.id === noteId);
        if (found) {
          setNote(found);
          return found;
        }
      } catch { /* fallback to create */ }
    }
    const r = await ax.post(endpointPrefix, {
      type,
      actualDate: form.actualDate,
      arrivalTime: form.arrivalTime || undefined,
      startTime: form.startTime || undefined,
      endTime: form.endTime || undefined,
      actualWeight: form.actualWeight ? parseFloat(form.actualWeight) : undefined,
      actualState: form.actualState || undefined,
      observations: form.observations || undefined,
      operatorName: operatorName || undefined,
    });
    setNote(r.data);
    if (arrivalGps) {
      try { await ax.patch(`${endpointPrefix}/${r.data.id}/arrival-gps`, arrivalGps); } catch { /* silent */ }
    }
    return r.data;
  };

  const saveDetails = async () => {
    setSaving(true);
    try {
      const current = await ensureNote();
      const r = await ax.patch(`${endpointPrefix}/${current.id}`, {
        actualDate: form.actualDate,
        arrivalTime: form.arrivalTime || null,
        startTime: form.startTime || null,
        endTime: form.endTime || null,
        actualWeight: form.actualWeight ? parseFloat(form.actualWeight) : null,
        actualState: form.actualState || null,
        observations: form.observations || null,
        operatorName,
      });
      setNote(r.data);
      return r.data;
    } finally { setSaving(false); }
  };

  const saveMerchandiseChecks = async () => {
    setSaving(true);
    try {
      const current = await ensureNote();
      const r = await ax.patch(`${endpointPrefix}/${current.id}`, {
        merchandiseChecks,
        reserveDescription: reserveDescription || null,
      });
      setNote(r.data);
      return r.data;
    } finally { setSaving(false); }
  };

  const handlePhotoUpload = async (payload) => {
    const current = await ensureNote();
    const r = await ax.post(`${endpointPrefix}/${current.id}/photos`, payload);
    setNote(r.data);
  };
  const handlePhotoRemove = async (photoId) => {
    if (!note) return;
    await ax.delete(`${endpointPrefix}/${note.id}/photos/${photoId}`);
    setNote(prev => prev ? { ...prev, photos: (prev.photos || []).filter(p => p.id !== photoId) } : prev);
  };

  const handleDownloadPdf = async () => {
    if (!note) return;
    try {
      const r = await ax.get(`${endpointPrefix}/${note.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${note.noteNumber}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast({ title: 'Erreur PDF', description: extractErr(err), variant: 'destructive' }); }
  };

  const markStepCompleted = (idx) => setCompletedSteps(prev => Array.from(new Set([...prev, idx])));

  const goNext = async () => {
    try {
      if (isLoading) {
        if (stepIndex === 0) await ensureNote();
        else if (stepIndex === 1) await saveDetails();
      } else {
        if (stepIndex === 0) await ensureNote();
        else if (stepIndex === 1) await saveMerchandiseChecks();
        else if (stepIndex === 2) await saveMerchandiseChecks();
      }
      markStepCompleted(stepIndex);
      setStepIndex(i => Math.min(i + 1, STEPS.length - 1));
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
    }
  };
  const goPrev = () => setStepIndex(i => Math.max(0, i - 1));

  // Validations par étape
  const hasNonConformity = merchandiseChecks.some(c => ['non_conform', 'not_verifiable'].includes(c.status));
  const allChecksDone = merchandiseChecks.length === 4;
  const reserveOk = !hasNonConformity || (hasNonConformity && reserveDescription.trim().length >= 5);
  const photoCount = (note?.photos || []).length;
  const sigCount = (note?.signatures || []).length;

  const canGoNext = (() => {
    if (isLoading) {
      if (stepIndex === 0) return true;
      if (stepIndex === 1) return form.endTime || form.startTime || form.arrivalTime;
      if (stepIndex === 2) return photoCount >= 2;
      if (stepIndex === 3) return sigCount >= 2;
      return false;
    } else {
      if (stepIndex === 0) return true;
      if (stepIndex === 1) return allChecksDone;
      if (stepIndex === 2) return reserveOk;
      if (stepIndex === 3) return photoCount >= 2;
      return false;
    }
  })();

  const isFullySigned = sigCount >= 2;

  // Statut du bon (PM Spec UI)
  const noteStatus = (() => {
    if (isFullySigned) {
      if (!isLoading && hasNonConformity) return { label: 'Avec réserve', color: 'orange' };
      return { label: 'Complet', color: 'emerald' };
    }
    if (note?.id) return { label: 'En cours', color: 'emerald' };
    return { label: 'Brouillon', color: 'slate' };
  })();

  return (
    <div className="space-y-4" data-testid={`wizard-${type}`}>
      {/* Header avec type, n° et statut */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0">
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-semibold text-slate-800 leading-tight">Bon de {titleAction}</div>
            <div className="text-xs font-mono text-slate-500">{note?.noteNumber || '—'}</div>
          </div>
          <Badge className={`text-xs font-medium border ${
            noteStatus.color === 'emerald' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
            noteStatus.color === 'orange' ? 'bg-orange-50 text-orange-700 border-orange-200' :
            'bg-slate-50 text-slate-600 border-slate-200'
          }`}>
            <Circle className={`w-2 h-2 mr-1.5 fill-current ${
              noteStatus.color === 'emerald' ? 'text-emerald-500' :
              noteStatus.color === 'orange' ? 'text-orange-500' : 'text-slate-400'
            }`} />
            {noteStatus.label}
          </Badge>
        </CardContent>
      </Card>

      {/* Stepper */}
      <Card className="border-slate-200">
        <CardContent className="p-3 px-4">
          <WizardStepperHorizontal
            steps={STEPS}
            currentIndex={stepIndex}
            completedIndexes={completedSteps}
            onStepClick={setStepIndex}
          />
        </CardContent>
      </Card>

      {/* Layout 2 cols : main + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-5 space-y-4 min-h-[400px]">
            {/* Step content */}
            {isLoading ? renderLoadingStep({
              stepIndex, operation, operatorName, arrivalGps, gpsLoading, captureGps,
              form, setForm, type, note, requiredLabels: ['marchandise', 'vue_ensemble'],
              onPhotoUpload: handlePhotoUpload, onPhotoRemove: handlePhotoRemove,
              sitePartyKey, isLoading,
              endpointPrefix, authHeaders,
              partyScope, linkId,
              onSigned: (updated) => setNote(updated),
              onDownloadPdf: handleDownloadPdf,
            }) : renderUnloadingStep({
              stepIndex, operation, operatorName, arrivalGps, gpsLoading, captureGps,
              form, setForm, type, note, requiredLabels: ['marchandise', 'vue_ensemble'],
              merchandiseChecks, setMerchandiseChecks,
              reserveDescription, setReserveDescription,
              hasNonConformity, allChecksDone,
              onPhotoUpload: handlePhotoUpload, onPhotoRemove: handlePhotoRemove,
              sitePartyKey, endpointPrefix, authHeaders,
              partyScope, linkId,
              onSigned: (updated) => setNote(updated),
              onDownloadPdf: handleDownloadPdf,
            })}
          </CardContent>
          {/* Footer nav */}
          <div className="border-t px-5 py-3 flex items-center justify-between bg-slate-50/40 rounded-b-lg">
            <Button variant="outline" size="sm"
                    disabled={stepIndex === 0}
                    onClick={goPrev}
                    data-testid={`${type}-wizard-prev`}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
            </Button>
            {stepIndex < STEPS.length - 1 ? (
              <Button size="sm"
                      disabled={!canGoNext || saving}
                      onClick={goNext}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`${type}-wizard-next`}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                Suivant <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button size="sm"
                      disabled={!isFullySigned}
                      onClick={handleDownloadPdf}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`${type}-wizard-finish`}>
                <Download className="w-4 h-4 mr-1" /> Télécharger le PDF
              </Button>
            )}
          </div>
        </Card>

        {/* Sidebar */}
        <div className="lg:sticky lg:top-4 self-start">
          <WizardSidebar
            note={note}
            operation={operation}
            type={type}
            currentStep={stepIndex}
            totalSteps={STEPS.length}
            gps={arrivalGps}
            operatorName={operatorName}
            stepLabels={STEPS}
          />
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// CHARGEMENT — Étapes
// ────────────────────────────────────────────────────────────────
function renderLoadingStep(p) {
  switch (p.stepIndex) {
    case 0: return <Step1Verification {...p} />;
    case 1: return <Step2DetailsLoading {...p} />;
    case 2: return (
      <div data-testid="step3-photos-loading">
        <PhotoUploadCard
          photos={p.note?.photos || []}
          requiredLabels={p.requiredLabels}
          maxPhotos={10}
          onUpload={p.onPhotoUpload}
          onRemove={p.onPhotoRemove}
          blockingMessage="Ajoutez les 2 photos obligatoires avant de passer à la signature."
        />
      </div>
    );
    case 3: return <Step4Signatures {...p} />;
    case 4: return <Step5Summary {...p} />;
    default: return null;
  }
}

// ────────────────────────────────────────────────────────────────
// DÉCHARGEMENT — Étapes
// ────────────────────────────────────────────────────────────────
function renderUnloadingStep(p) {
  switch (p.stepIndex) {
    case 0: return <Step1Verification {...p} />;
    case 1: return (
      <div data-testid="step2-merchandise-check">
        <MerchandiseChecksList
          checks={p.merchandiseChecks}
          onChange={p.setMerchandiseChecks}
          reserveDescription={p.reserveDescription}
          onReserveChange={p.setReserveDescription}
        />
        <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-md flex items-start gap-2 text-xs text-blue-700"
             data-testid="merchandise-info">
          <span className="mt-0.5">ℹ</span>
          <span>Vous pourrez ajouter des photos et collecter la signature à l'étape suivante.</span>
        </div>
      </div>
    );
    case 2: return <StepReservesUnloading {...p} />;
    case 3: return (
      <div data-testid="step4-photos-unloading">
        <PhotoUploadCard
          photos={p.note?.photos || []}
          requiredLabels={p.requiredLabels}
          maxPhotos={10}
          onUpload={p.onPhotoUpload}
          onRemove={p.onPhotoRemove}
          blockingMessage={p.hasNonConformity
            ? "Une réserve a été signalée — ajoutez les 2 photos preuves ci-dessous."
            : "Ajoutez les 2 photos obligatoires avant de passer à la signature."}
        />
      </div>
    );
    case 4: return (
      <div className="space-y-4">
        <Step4Signatures {...p} />
        <Step5Summary {...p} compact />
      </div>
    );
    default: return null;
  }
}

// ────────────────────────────────────────────────────────────────
// COMPOSANTS COMMUNS DES ÉTAPES
// ────────────────────────────────────────────────────────────────
const Step1Verification = ({ operation, operatorName, arrivalGps: gps, gpsLoading, captureGps }) => {
  const ref = operation?.reference || operation?.operationNumber || operation?.requestNumber || '—';
  return (
    <div className="space-y-4" data-testid="step1-verification">
      <div>
        <h3 className="text-base font-semibold text-slate-800">1. Vérifiez les informations de l'opération</h3>
        <p className="text-xs text-slate-500 mt-1">
          Confirmez le contexte de l'opération avant de poursuivre. Les données ci-dessous sont en lecture seule.
        </p>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2.5 text-sm">
        <Row label="Référence" value={<span className="font-mono font-semibold text-slate-800">{ref}</span>} />
        <Row label="Client" value={operation?.clientName || '—'} />
        <Row label="Trajet" value={`${operation?.departure || '—'} → ${operation?.arrival || '—'}`} />
        <Row label="Marchandise" value={`${operation?.merchandiseType || '—'}${operation?.weight ? ` (${operation.weight} T)` : ''}`} />
        <Row label="Véhicule" value={`${operation?.vehicleType || '—'}${operation?.licensePlate ? ` — ${operation.licensePlate}` : ''}`} />
        {operation?.driverName && <Row label="Chauffeur" value={operation.driverName} />}
      </div>
      <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-xs">
        <Lock className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
        <span>Connecté en tant que <strong>{operatorName}</strong> (verrouillé)</span>
      </div>
      <div className={`p-3 rounded-lg border ${gps ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}`}
           data-testid="gps-block">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className={`w-4 h-4 ${gps ? 'text-emerald-700' : 'text-orange-700'}`} />
            <div className="text-xs">
              {gps ? (
                <>
                  <div className="font-semibold text-emerald-800">Position GPS captée</div>
                  <div className="font-mono text-emerald-700">{gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</div>
                </>
              ) : gpsLoading ? (
                <span className="text-orange-700">Capture GPS en cours…</span>
              ) : (
                <span className="text-orange-700">GPS non capté (optionnel)</span>
              )}
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={gpsLoading} onClick={captureGps}
                  data-testid="gps-recapture-btn">
            {gpsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}
            <span className="ml-1 text-xs">{gps ? 'Recapter' : 'Capter GPS'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-start gap-3 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-800 text-right">{value}</span>
  </div>
);

const Step2DetailsLoading = ({ form, setForm, type }) => {
  const nowHHMM = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const setNow = (field) => setForm(f => ({ ...f, [field]: nowHHMM() }));

  return (
    <div className="space-y-4" data-testid="step2-details">
      <div>
        <h3 className="text-base font-semibold text-slate-800">2. Détails du chargement sur site</h3>
        <p className="text-xs text-slate-500 mt-1">
          Renseignez les informations terrain. Utilisez les boutons "▶ Début" et "▶ Fin" pour horodater en un clic.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Date</Label>
          <Input type="date" value={form.actualDate}
                 onChange={e => setForm(f => ({ ...f, actualDate: e.target.value }))}
                 className="h-10" data-testid={`${type}-actual-date`} />
        </div>
        <div>
          <Label className="text-xs">Heure d'arrivée</Label>
          <div className="flex gap-1">
            <Input type="time" value={form.arrivalTime}
                   onChange={e => setForm(f => ({ ...f, arrivalTime: e.target.value }))}
                   className="h-10 flex-1" data-testid={`${type}-arrival-time`} />
            <Button size="sm" variant="outline" className="h-10 px-2"
                    onClick={() => setNow('arrivalTime')}
                    data-testid={`${type}-arrival-now`}>
              <Clock className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Heure de début</Label>
          <div className="flex gap-1">
            <Input type="time" value={form.startTime}
                   onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
                   className="h-10 flex-1" data-testid={`${type}-start-time`} />
            <Button size="sm" variant="default" className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-xs"
                    onClick={() => setNow('startTime')}
                    data-testid={`${type}-start-now`}>
              ▶ Début
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs">Heure de fin</Label>
          <div className="flex gap-1">
            <Input type="time" value={form.endTime}
                   onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))}
                   className="h-10 flex-1" data-testid={`${type}-end-time`} />
            <Button size="sm" variant="default" className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-xs"
                    onClick={() => setNow('endTime')}
                    data-testid={`${type}-end-now`}>
              ▶ Fin
            </Button>
          </div>
        </div>
      </div>
      <div>
        <Label className="text-xs">Poids constaté (Tonnes)</Label>
        <Input type="number" step="any" value={form.actualWeight}
               onChange={e => setForm(f => ({ ...f, actualWeight: e.target.value }))}
               className="h-10" placeholder="Ex: 12.5" data-testid={`${type}-weight`} />
      </div>
      <div>
        <Label className="text-xs">État / Réserves</Label>
        <Textarea rows={5} value={form.actualState}
                  onChange={e => setForm(f => ({ ...f, actualState: e.target.value }))}
                  className="text-sm resize-none"
                  placeholder="Ex: Marchandise reçue en bon état, aucune réserve."
                  data-testid={`${type}-state`} />
      </div>
      <div>
        <Label className="text-xs">Observations supplémentaires</Label>
        <Textarea rows={3} value={form.observations}
                  onChange={e => setForm(f => ({ ...f, observations: e.target.value }))}
                  className="text-sm resize-none"
                  placeholder="Anomalies, retards, etc."
                  data-testid={`${type}-obs`} />
      </div>
    </div>
  );
};

const StepReservesUnloading = ({ hasNonConformity, allChecksDone, reserveDescription, setReserveDescription }) => {
  if (!allChecksDone) {
    return (
      <div className="text-sm text-slate-400 italic">
        Complétez d'abord les contrôles marchandise à l'étape 2.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="step3-reserves">
      <div>
        <h3 className="text-base font-semibold text-slate-800">3. Réserves & anomalies</h3>
        <p className="text-xs text-slate-500 mt-1">
          {hasNonConformity
            ? "Une non-conformité a été signalée à l'étape précédente. Décrivez précisément la réserve ci-dessous."
            : "Aucune non-conformité signalée. Vous pouvez ajouter une note libre ou passer à l'étape suivante."}
        </p>
      </div>
      {hasNonConformity ? (
        <div className="border-2 border-orange-300 bg-orange-50/40 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center">
              <span className="text-orange-600 text-sm">!</span>
            </div>
            <div>
              <div className="text-sm font-semibold text-orange-700">Anomalie ou réserve à décrire</div>
              <div className="text-xs text-orange-600">Obligatoire — minimum 5 caractères</div>
            </div>
          </div>
          <Textarea
            rows={6} value={reserveDescription}
            onChange={(e) => setReserveDescription(e.target.value.slice(0, 1000))}
            placeholder="Décrivez en détail : ce qui a été constaté, sur quels colis, conséquences éventuelles…"
            className="text-sm bg-white"
            data-testid="reserve-description-input-step3"
          />
          <div className="flex justify-between text-[11px] text-slate-500">
            <span>{reserveDescription.length < 5 ? 'Minimum 5 caractères' : 'OK'}</span>
            <span>{reserveDescription.length} / 1000</span>
          </div>
          <div className="text-xs text-orange-700 bg-orange-100/60 rounded p-2">
            <strong>Photos preuves recommandées</strong> à l'étape suivante (étape 4).
          </div>
        </div>
      ) : (
        <div className="border-2 border-emerald-200 bg-emerald-50/30 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div className="text-sm font-semibold text-emerald-700">Aucune réserve signalée</div>
          </div>
          <p className="text-xs text-slate-600">
            Tous les contrôles marchandise ont été marqués "Conforme". Vous pouvez ajouter une note libre ci-dessous (facultatif).
          </p>
          <Textarea
            rows={3} value={reserveDescription}
            onChange={(e) => setReserveDescription(e.target.value.slice(0, 1000))}
            placeholder="Note libre (facultatif)"
            className="text-sm bg-white"
            data-testid="reserve-note-step3"
          />
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Étape Signatures
// ────────────────────────────────────────────────────────────────
const Step4Signatures = ({ note, sitePartyKey, isLoading, operatorName,
                          operation, endpointPrefix, authHeaders, onSigned,
                          partyScope = null, linkId = null }) => {
  if (!note) return <div className="text-sm text-slate-400 italic">Le bon doit être créé avant de signer.</div>;
  const sigCount = (note.signatures || []).length;
  // Fallback opérateur Baobab : opérateur affecté ou Field Ops affecté
  const baobabRepName = (
    operation?.assignedOperatorName ||
    operation?.assignedFieldOpsName ||
    operatorName ||
    ''
  );

  // PM Spec 07/05/2026 — Filtrage par partyScope (lien ciblé : on ne montre QUE le slot concerné)
  const isVisible = (party) => !partyScope || partyScope === party;

  return (
    <div className="space-y-4" data-testid="step4-signatures">
      <div>
        <h3 className="text-base font-semibold text-slate-800">
          {isLoading ? '4. Signatures électroniques' : '5. Signature & finalisation'} ({sigCount}/2 requises)
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          {partyScope
            ? `Vous signez en tant que « ${partyLabel(partyScope, isLoading)} ». Les autres signataires utilisent leur propre lien.`
            : `Faites signer les 2 parties prenantes obligatoires : représentant ${isLoading ? 'expéditeur' : 'destinataire'} et chauffeur Baobab. Vous pouvez ajouter une 3ème signature optionnelle (Opérateur ou Field Ops).`}
        </p>
      </div>

      {/* PM Spec 07/05/2026 — Bandeau "Pas d'agent sur place ?" — visible en mode admin (pas partyScope) */}
      {!partyScope && (
        <NoAgentToolbar note={note} operation={operation}
                        endpointPrefix={endpointPrefix} authHeaders={authHeaders}
                        isLoading={isLoading} onNoteUpdated={onSigned} />
      )}

      {isVisible(sitePartyKey) && (
        <SigBlock
          label={isLoading ? "Représentant site expéditeur" : "Représentant site destinataire"}
          party={sitePartyKey} note={note}
          operation={operation}
          captureIdPhoto
          requireIdPhoto={!isLoading /* PM Spec : photo CNI obligatoire en déchargement */}
          endpointPrefix={endpointPrefix} authHeaders={authHeaders} onSigned={onSigned}
          linkId={partyScope === sitePartyKey ? linkId : null}
        />
      )}
      {isVisible('driver') && (
        <SigBlock
          label="Chauffeur Baobab" party="driver" note={note}
          defaultName={operation?.driverName || ''} defaultRole="Chauffeur"
          endpointPrefix={endpointPrefix} authHeaders={authHeaders} onSigned={onSigned}
          linkId={partyScope === 'driver' ? linkId : null}
        />
      )}
      {isVisible('observer') && (
        <SigBlock
          label="Opérateur / Field Ops Baobab"
          party="observer" note={note}
          defaultName={baobabRepName}
          defaultRole={operation?.assignedFieldOpsName ? 'Field Ops' : 'Opérateur'}
          optional
          endpointPrefix={endpointPrefix} authHeaders={authHeaders} onSigned={onSigned}
          linkId={partyScope === 'observer' ? linkId : null}
        />
      )}
    </div>
  );
};

// Map party → label lisible (pour message UX en mode lien ciblé)
const partyLabel = (party, isLoading) => {
  if (party === 'shipper') return 'Représentant site expéditeur';
  if (party === 'consignee') return 'Représentant site destinataire';
  if (party === 'driver') return 'Chauffeur Baobab';
  if (party === 'observer') return 'Opérateur / Field Ops Baobab';
  return party;
};

// ────────────────────────────────────────────────────────────────
// PM Spec 07/05/2026 — Bandeau "Pas d'agent sur place ?" (NoAgentToolbar)
// Affiché en haut de l'étape Signatures côté admin (jamais sur lien ciblé).
// Permet 2 modes dégradés : (1) télécharger PDF vierge à imprimer, (2) générer
// un lien ciblé à envoyer au signataire (chauffeur, représentant client…).
// ────────────────────────────────────────────────────────────────
const NoAgentToolbar = ({ note, operation, endpointPrefix, authHeaders, isLoading, onNoteUpdated }) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [downloadingBlank, setDownloadingBlank] = useState(false);
  const links = note?.signatureLinks || [];
  const activeLinks = links.filter(l => !l.revokedAt && !l.signedAt);
  const ax = axios.create({ headers: authHeaders || {} });

  const handleDownloadBlank = async () => {
    setDownloadingBlank(true);
    try {
      const r = await ax.get(`${endpointPrefix}/${note.id}/pdf/blank`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${note.noteNumber}-vierge.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'PDF vierge téléchargé', description: 'À imprimer pour la signature papier.' });
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
    } finally { setDownloadingBlank(false); }
  };

  return (
    <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-3" data-testid="no-agent-toolbar">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-900">Pas d'agent Baobab sur place ?</div>
          <p className="text-xs text-amber-800/80 mt-0.5">
            Envoyez un lien personnalisé au chauffeur ou au représentant du client, ou imprimez un bon vierge.
          </p>
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-2">
        <Button size="sm" variant="outline"
                className="border-amber-300 bg-white hover:bg-amber-100 text-amber-900"
                onClick={() => setShowLinkModal(true)}
                data-testid="open-signature-link-modal">
          <Link2 className="w-3.5 h-3.5 mr-1.5" /> Générer un lien de signature
        </Button>
        <Button size="sm" variant="outline"
                className="border-amber-300 bg-white hover:bg-amber-100 text-amber-900"
                disabled={downloadingBlank}
                onClick={handleDownloadBlank}
                data-testid="download-blank-pdf-btn">
          {downloadingBlank ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
          Télécharger PDF vierge
        </Button>
        {activeLinks.length > 0 && (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
            {activeLinks.length} lien(s) actif(s)
          </Badge>
        )}
      </div>
      {/* Liste des liens existants */}
      {links.length > 0 && (
        <SignatureLinksList
          links={links}
          note={note}
          endpointPrefix={endpointPrefix}
          authHeaders={authHeaders}
          onChanged={onNoteUpdated}
        />
      )}

      {/* Modal de génération */}
      {showLinkModal && (
        <SignatureLinkModal
          note={note}
          operation={operation}
          isLoading={isLoading}
          endpointPrefix={endpointPrefix}
          authHeaders={authHeaders}
          onClose={() => setShowLinkModal(false)}
          onCreated={(updatedNote) => {
            onNoteUpdated?.(updatedNote);
            setShowLinkModal(false);
          }}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Liste des liens existants (avec actions revoke / re-copy / re-WhatsApp)
// ────────────────────────────────────────────────────────────────
const SignatureLinksList = ({ links, note, endpointPrefix, authHeaders, onChanged }) => {
  const ax = axios.create({ headers: authHeaders || {} });
  const sorted = [...links].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const [qrLink, setQrLink] = useState(null);

  const handleRevoke = async (link) => {
    if (!window.confirm('Révoquer ce lien ? Le destinataire ne pourra plus signer avec.')) return;
    try {
      const r = await ax.post(`${endpointPrefix}/${note.id}/signature-links/${link.id}/revoke`);
      onChanged?.(r.data);
      toast({ title: 'Lien révoqué' });
    } catch (err) { toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' }); }
  };

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(link.url);
      await ax.post(`${endpointPrefix}/${note.id}/signature-links/${link.id}/mark-sent`,
                    { channel: 'copy' }).catch(() => null);
      toast({ title: 'Lien copié', description: 'Collez-le dans WhatsApp/SMS.' });
    } catch { /* clipboard may fail */ }
  };

  const handleWhatsApp = async (link) => {
    const phone = (link.targetPhone || '').replace(/[^\d+]/g, '');
    const greet = link.targetName ? `Bonjour ${link.targetName},` : 'Bonjour,';
    const ref = note?.operationReference || note?.noteNumber || '';
    const text = encodeURIComponent(
      `${greet}\nMerci de signer le bon de ${note.type === 'loading' ? 'chargement' : 'déchargement'} ${ref} :\n${link.url}\n\nCode : ${link.code}`
    );
    const url = phone ? `https://wa.me/${phone.replace('+', '')}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, '_blank', 'noopener');
    await ax.post(`${endpointPrefix}/${note.id}/signature-links/${link.id}/mark-sent`,
                  { channel: 'whatsapp_manual' }).catch(() => null);
  };

  const handleSms = async (link) => {
    const phone = (link.targetPhone || '').replace(/[^\d+]/g, '');
    const ref = note?.operationReference || note?.noteNumber || '';
    const text = encodeURIComponent(
      `Bon ${ref} à signer : ${link.url} — Code : ${link.code}`
    );
    const url = phone ? `sms:${phone}?body=${text}` : `sms:?body=${text}`;
    window.open(url, '_blank', 'noopener');
    await ax.post(`${endpointPrefix}/${note.id}/signature-links/${link.id}/mark-sent`,
                  { channel: 'sms_manual' }).catch(() => null);
  };

  return (
    <div className="mt-3 space-y-1.5" data-testid="signature-links-list">
      <div className="text-[11px] font-medium text-amber-900 uppercase tracking-wide">Liens générés</div>
      {sorted.map(link => {
        const isActive = !link.revokedAt && !link.signedAt;
        const status = link.signedAt ? 'signé'
                     : link.revokedAt ? 'révoqué'
                     : 'actif';
        const color = link.signedAt ? 'emerald'
                     : link.revokedAt ? 'slate'
                     : 'amber';
        return (
          <div key={link.id}
               className={`p-2 rounded border text-xs flex items-center gap-2 ${
                 color === 'emerald' ? 'bg-emerald-50 border-emerald-200' :
                 color === 'amber' ? 'bg-white border-amber-200' :
                 'bg-slate-50 border-slate-200 opacity-60'
               }`}
               data-testid={`signature-link-item-${link.id}`}>
            <Badge className={`text-[10px] ${
              color === 'emerald' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
              color === 'amber' ? 'bg-amber-100 text-amber-700 border-amber-200' :
              'bg-slate-200 text-slate-600 border-slate-300'
            }`}>
              {partyLabel(link.party, note?.type === 'loading')} · {status}
            </Badge>
            {link.targetName && <span className="text-slate-700 truncate">{link.targetName}</span>}
            {link.targetPhone && (
              <span className="text-slate-500 font-mono text-[10px]">{link.targetPhone}</span>
            )}
            <span className="ml-auto flex items-center gap-1">
              {isActive && (
                <>
                  <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]"
                          onClick={() => handleCopy(link)}
                          data-testid={`copy-link-${link.id}`}>
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-slate-700"
                          onClick={() => setQrLink(link)}
                          data-testid={`qr-link-${link.id}`}
                          title="Afficher QR code">
                    <QrCode className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-emerald-700"
                          onClick={() => handleWhatsApp(link)}
                          data-testid={`whatsapp-link-${link.id}`}>
                    <MessageSquare className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]"
                          onClick={() => handleSms(link)}
                          data-testid={`sms-link-${link.id}`}>
                    <Phone className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-red-600"
                          onClick={() => handleRevoke(link)}
                          data-testid={`revoke-link-${link.id}`}>
                    <X className="w-3 h-3" />
                  </Button>
                </>
              )}
              {link.signedAt && <Check className="w-3 h-3 text-emerald-600" />}
            </span>
          </div>
        );
      })}
      {/* QR Modal */}
      {qrLink && (
        <SignatureLinkQRModal
          link={qrLink}
          note={note}
          onClose={() => setQrLink(null)}
          onShown={async (channel) => {
            try {
              await ax.post(`${endpointPrefix}/${note.id}/signature-links/${qrLink.id}/mark-sent`,
                            { channel });
            } catch { /* silent */ }
          }}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Modal de génération d'un nouveau lien ciblé
// ────────────────────────────────────────────────────────────────
const SignatureLinkModal = ({ note, operation, isLoading,
                              endpointPrefix, authHeaders, onClose, onCreated }) => {
  const sitePartyKey = isLoading ? 'shipper' : 'consignee';
  const partyOptions = [
    { value: sitePartyKey, label: isLoading ? 'Représentant site expéditeur' : 'Représentant site destinataire' },
    { value: 'driver', label: 'Chauffeur Baobab' },
    { value: 'observer', label: 'Opérateur / Field Ops Baobab (optionnel)' },
  ];

  const [party, setParty] = useState(sitePartyKey);
  const [targetName, setTargetName] = useState('');
  const [targetPhone, setTargetPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Q6 — Pré-remplissage téléphone selon la party
  useEffect(() => {
    if (party === 'shipper' || party === 'consignee') {
      setTargetPhone(note?.clientPhone || '');
    } else if (party === 'driver') {
      setTargetPhone(note?.driverPhone || operation?.driverPhone || '');
      setTargetName(note?.driverName || operation?.driverName || '');
    } else {
      setTargetPhone('');
      setTargetName(operation?.assignedOperatorName || operation?.assignedFieldOpsName || '');
    }
  }, [party, note, operation]);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const ax = axios.create({ headers: authHeaders || {} });
      const r = await ax.post(`${endpointPrefix}/${note.id}/signature-links`, {
        party,
        targetName: targetName.trim() || null,
        targetPhone: targetPhone.trim() || null,
      });
      const link = r.data?.link;
      const updatedNote = r.data?.note;
      // Auto-copy pour confort UX
      if (link?.url) {
        try { await navigator.clipboard.writeText(link.url); } catch { /* noop */ }
        toast({ title: 'Lien généré', description: 'Le lien a été copié dans le presse-papiers.' });
      }
      onCreated?.(updatedNote);
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="signature-link-modal">
      <Card className="w-full max-w-md" onClick={e => e.stopPropagation()}>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Send className="w-4 h-4 text-emerald-600" /> Lien de signature à distance
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Le destinataire ouvre le lien, n'a accès qu'à SA signature, valide. Vous pouvez aussi remettre un PDF papier.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Pour quel signataire ?</Label>
            <select value={party} onChange={e => setParty(e.target.value)}
                    className="w-full h-10 text-sm border border-slate-200 rounded-md px-2 bg-white"
                    data-testid="link-party-select">
              {partyOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Nom (optionnel)</Label>
              <Input value={targetName} onChange={e => setTargetName(e.target.value)}
                     placeholder="Ex: M. Konan" className="h-10 text-sm"
                     data-testid="link-target-name" />
            </div>
            <div>
              <Label className="text-xs">Téléphone (optionnel)</Label>
              <Input value={targetPhone} onChange={e => setTargetPhone(e.target.value)}
                     placeholder="+225..." className="h-10 text-sm"
                     data-testid="link-target-phone" />
            </div>
          </div>

          <div className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 flex items-start gap-2">
            <RotateCcw className="w-3 h-3 mt-0.5 shrink-0" />
            <span>Si un lien actif existe déjà pour ce signataire, il sera automatiquement révoqué.</span>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={onClose}>
              Annuler
            </Button>
            <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    disabled={submitting} onClick={handleCreate}
                    data-testid="link-create-confirm">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Link2 className="w-4 h-4 mr-1.5" />}
              Générer le lien
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Bloc signature avec canvas redimensionné dynamiquement (DPR-aware).
 * Fix bug "tracé hors cadre" : le canvas interne adopte exactement les
 * dimensions CSS du conteneur via ResizeObserver, multipliées par devicePixelRatio
 * pour un rendu HD sans décalage de coordonnées.
 */
const SigBlock = ({ label, party, note, defaultName = '', defaultRole = '',
                    optional = false,
                    captureIdPhoto = false, requireIdPhoto = false,
                    operation,
                    endpointPrefix, authHeaders, onSigned,
                    linkId = null }) => {
  const sigRef = useRef(null);
  const wrapperRef = useRef(null);
  const idPhotoInputRef = useRef(null);
  const existing = (note?.signatures || []).find(s => s.party === party);
  const [name, setName] = useState(existing?.name || defaultName);
  const [role, setRole] = useState(existing?.role || defaultRole);
  const [showPad, setShowPad] = useState(!existing);
  const [submitting, setSubmitting] = useState(false);
  // PM Spec — pièce d'identité représentant
  const [idDocumentType, setIdDocumentType] = useState(existing?.idDocumentType || 'cni');
  const [idDocumentNumber, setIdDocumentNumber] = useState(existing?.idDocumentNumber || '');
  const [idPhotoFile, setIdPhotoFile] = useState(null);
  const [idPhotoUploading, setIdPhotoUploading] = useState(false);
  const [idPhotoId, setIdPhotoId] = useState(existing?.idPhotoId || null);

  // Resize canvas pour matcher le conteneur (fix tracé hors cadre)
  // Évite de clear le tracé quand seul un reflow mineur se produit (clavier virtuel, etc.)
  useEffect(() => {
    if (!showPad) return;
    const wrapper = wrapperRef.current;
    if (!wrapper || !sigRef.current) return;

    let lastWidth = 0;

    const resize = (force = false) => {
      const canvas = sigRef.current?.getCanvas?.();
      if (!canvas || !wrapper) return;
      const cssW = wrapper.clientWidth;
      // N'agir que si la largeur a vraiment changé (>2px) pour éviter perte du tracé sur reflow mineur
      if (!force && Math.abs(cssW - lastWidth) < 2) return;
      lastWidth = cssW;

      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const cssH = 140;
      // Sauvegarde du tracé existant avant resize
      const hadDrawing = !sigRef.current.isEmpty?.();
      const dataUrl = hadDrawing ? sigRef.current.toDataURL() : null;

      canvas.width = cssW * ratio;
      canvas.height = cssH * ratio;
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);
      sigRef.current.clear();
      if (dataUrl) {
        // Restaure le tracé en respectant les dimensions CSS
        sigRef.current.fromDataURL(dataUrl, { width: cssW, height: cssH });
      }
    };

    resize(true); // init forcé
    const ro = new ResizeObserver(() => resize(false));
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [showPad]);

  const handleClear = () => sigRef.current?.clear();
  const captureGpsLocal = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000, enableHighAccuracy: false }
    );
  });

  // Compresser un fichier image vers base64 JPEG (max 1280px, q=0.7) — même pattern que PhotoCapture
  const compressFileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 1280;
        const ratio = Math.min(1, MAX_W / img.width);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        const sizeBytes = Math.round((dataUrl.length * 3) / 4);
        resolve({ base64: dataUrl, fileSize: sizeBytes });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // PM Spec — Upload de la photo CNI vers /photos avec label="representative_id"
  const uploadIdPhoto = async () => {
    if (!idPhotoFile) return null;
    setIdPhotoUploading(true);
    try {
      const { base64, fileSize } = await compressFileToBase64(idPhotoFile);
      const ax = axios.create({ headers: authHeaders || {} });
      const r = await ax.post(`${endpointPrefix}/${note.id}/photos`, {
        label: 'representative_id',
        base64,
        fileSize,
      });
      // Find the newly added photo (the latest one with our label)
      const photos = r.data?.photos || [];
      const addedPhoto = photos
        .filter(p => p.label === 'representative_id')
        .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))[0];
      const photoId = addedPhoto?.id || r.data?.photo?.id || r.data?.id;
      setIdPhotoId(photoId);
      return photoId;
    } catch (err) {
      toast({ title: 'Échec upload pièce', description: extractErr(err), variant: 'destructive' });
      return null;
    } finally { setIdPhotoUploading(false); }
  };

  const handleSubmit = async () => {
    if (!name?.trim() || name.trim().length < 2) {
      toast({ title: 'Nom requis', variant: 'destructive' }); return;
    }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast({ title: 'Signature manquante', variant: 'destructive' }); return;
    }
    if (requireIdPhoto && !idPhotoId && !idPhotoFile) {
      toast({ title: 'Photo CNI requise', description: 'La photo de la pièce d\'identité du représentant est obligatoire pour le déchargement.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      // 1) Si photo en attente, l'uploader
      let photoId = idPhotoId;
      if (idPhotoFile && !photoId) {
        photoId = await uploadIdPhoto();
        if (requireIdPhoto && !photoId) { setSubmitting(false); return; }
      }
      const dataUrl = sigRef.current.toDataURL('image/png');
      const gps = await captureGpsLocal();
      const ax = axios.create({ headers: authHeaders || {} });
      const r = await ax.post(`${endpointPrefix}/${note.id}/sign`, {
        party, name: name.trim(), role: role.trim() || null,
        signatureBase64: dataUrl, gpsCoords: gps,
        deviceInfo: navigator.userAgent.slice(0, 120),
        // PM Spec — Identification représentant signataire
        idDocumentType: captureIdPhoto ? idDocumentType : null,
        idDocumentNumber: captureIdPhoto ? (idDocumentNumber.trim() || null) : null,
        idPhotoId: captureIdPhoto ? photoId : null,
        // PM Spec 07/05/2026 — Lien ciblé : on tag pour tracking
        linkId: linkId || null,
      });
      onSigned?.(r.data);
      setShowPad(false);
      toast({ title: 'Signé', description: `${label} — signature enregistrée` });
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <Card className={existing ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200'}
          data-testid={`sig-block-${party}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            {label}
            {optional && !existing && (
              <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px] uppercase tracking-wide">
                Optionnel
              </Badge>
            )}
          </div>
          {existing && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
              <Check className="w-3 h-3 mr-1" /> Signé
            </Badge>
          )}
        </div>
        {existing && !showPad ? (
          <div className="space-y-1 text-xs">
            <div><strong>{existing.name}</strong>{existing.role ? ` — ${existing.role}` : ''}</div>
            <div className="border border-dashed border-slate-300 bg-white rounded p-1 flex justify-center">
              <img src={existing.signatureBase64} alt="signature"
                   className="max-h-14 object-contain" />
            </div>
            {existing.gpsCoords && (
              <div className="text-[10px] text-slate-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {existing.gpsCoords.lat.toFixed(4)}, {existing.gpsCoords.lng.toFixed(4)}
              </div>
            )}
            <Button variant="link" size="sm" className="h-6 p-0 text-xs"
                    onClick={() => setShowPad(true)} data-testid={`resign-${party}`}>
              Re-signer
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Input value={name} onChange={e => setName(e.target.value)}
                     placeholder="Nom" className="h-9 text-sm"
                     data-testid={`sig-name-${party}`} />
              <Input value={role} onChange={e => setRole(e.target.value)}
                     placeholder="Fonction" className="h-9 text-sm" />
            </div>

            {captureIdPhoto && (
              <div className="space-y-2 pt-1 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-600">
                    Pièce d'identité du représentant
                    {requireIdPhoto && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  {requireIdPhoto && (
                    <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                      Obligatoire (déchargement)
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={idDocumentType}
                    onChange={(e) => setIdDocumentType(e.target.value)}
                    className="h-9 text-sm border border-slate-200 rounded-md px-2"
                    data-testid={`sig-id-type-${party}`}
                  >
                    <option value="cni">CNI</option>
                    <option value="passport">Passeport</option>
                    <option value="driving_license">Permis</option>
                    <option value="other">Autre</option>
                  </select>
                  <Input
                    value={idDocumentNumber}
                    onChange={(e) => setIdDocumentNumber(e.target.value)}
                    placeholder="N° pièce"
                    className="h-9 text-sm"
                    data-testid={`sig-id-num-${party}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    ref={idPhotoInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => setIdPhotoFile(e.target.files?.[0] || null)}
                    data-testid={`sig-id-photo-input-${party}`}
                  />
                  <Button
                    type="button"
                    variant={idPhotoFile || idPhotoId ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => idPhotoInputRef.current?.click()}
                    className={idPhotoFile || idPhotoId ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                    data-testid={`sig-id-photo-btn-${party}`}
                  >
                    {idPhotoId ? '✓ Photo CNI uploadée' : idPhotoFile ? `✓ ${idPhotoFile.name.slice(0, 18)}…` : '📷 Photo CNI'}
                  </Button>
                  {(idPhotoFile || idPhotoId) && (
                    <Button
                      type="button" variant="ghost" size="sm"
                      onClick={() => { setIdPhotoFile(null); setIdPhotoId(null); }}
                      className="h-8 text-xs text-slate-400"
                    >
                      Retirer
                    </Button>
                  )}
                </div>
                {!requireIdPhoto && (
                  <p className="text-[10px] text-slate-400 italic">
                    Optionnel pour un chargement. Recommandé pour faciliter une éventuelle vérification.
                  </p>
                )}
              </div>
            )}

            <div ref={wrapperRef}
                 className="border-2 border-dashed border-slate-300 rounded bg-white overflow-hidden"
                 style={{ height: 140 }}>
              <SignatureCanvas
                ref={sigRef}
                penColor="#111"
                canvasProps={{ className: 'touch-none block',
                               'data-testid': `sig-canvas-${party}` }}
              />
            </div>
            <div className="flex justify-between gap-2">
              <Button variant="ghost" size="sm" onClick={handleClear}>
                <Eraser className="w-3 h-3 mr-1" /> Effacer
              </Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={submitting} onClick={handleSubmit}
                      data-testid={`sig-submit-${party}`}>
                {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                Valider
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ────────────────────────────────────────────────────────────────
// Étape Récap
// ────────────────────────────────────────────────────────────────
const Step5Summary = ({ note, form, type, operation, operatorName, arrivalGps: gps,
                       merchandiseChecks = [], reserveDescription = '',
                       onDownloadPdf, compact = false }) => {
  const isLoading = type === 'loading';
  const sigCount = (note?.signatures || []).length;
  const photoCount = (note?.photos || []).length;
  const ready = sigCount >= 2 && photoCount >= 2;
  const hasNonConformity = merchandiseChecks.some(c => ['non_conform', 'not_verifiable'].includes(c.status));

  return (
    <div className={`space-y-3 ${compact ? '' : ''}`} data-testid="step5-summary">
      {!compact && (
        <div>
          <h3 className="text-base font-semibold text-slate-800">5. Récapitulatif & finalisation</h3>
          <p className="text-xs text-slate-500 mt-1">Vérifiez les informations puis téléchargez le PDF signé.</p>
        </div>
      )}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5 text-xs">
        <Row label="Référence opération" value={operation?.reference || '—'} />
        <Row label="Type" value={isLoading ? 'Bon de chargement' : 'Bon de déchargement'} />
        <Row label="Numéro" value={<span className="font-mono">{note?.noteNumber || '—'}</span>} />
        <Row label="Date" value={form.actualDate} />
        <Row label="Heures" value={`${form.arrivalTime || '—'} → ${form.endTime || form.startTime || '—'}`} />
        {form.actualWeight && <Row label="Poids constaté" value={`${form.actualWeight} T`} />}
        <Row label="Photos" value={photoCount} />
        <Row label="Signatures" value={`${sigCount} / 2`} />
        {!isLoading && merchandiseChecks.length > 0 && (
          <Row label="Contrôles" value={`${merchandiseChecks.length} / 4`} />
        )}
        <Row label="Opérateur" value={operatorName} />
        {gps && <Row label="GPS arrivée" value={`${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}`} />}
      </div>
      {!isLoading && hasNonConformity && (
        <div className="border-2 border-orange-300 bg-orange-50/40 rounded-md p-3 text-xs text-orange-700">
          <strong>⚠ Réserve signalée :</strong> {reserveDescription || '(non décrite)'}
        </div>
      )}
      {!ready && (
        <div className="flex items-center gap-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
          <AlertCircle className="w-4 h-4" />
          <span>Pour finaliser, ajoutez {photoCount < 2 ? 'les photos obligatoires' : ''}{photoCount < 2 && sigCount < 2 ? ' et ' : ''}{sigCount < 2 ? 'les 2 signatures' : ''}.</span>
        </div>
      )}
      {ready && (
        <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
          <CheckCircle2 className="w-4 h-4" />
          <span>Bon complet. Le statut de l'opération sera mis à jour automatiquement.</span>
        </div>
      )}
      <Button onClick={onDownloadPdf} disabled={!note}
              className="w-full h-10 bg-emerald-600 hover:bg-emerald-700"
              data-testid={`${type}-finalize-pdf`}>
        <Download className="w-4 h-4 mr-2" /> Télécharger le PDF signé
      </Button>
    </div>
  );
};

export default LoadingNoteWizard;
